import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import {
  Award, Users, Building2, Shield, ArrowRight, PiggyBank, Briefcase, MapPin, Sparkles, Heart,
  FileText, Scale, ExternalLink, AlertTriangle, CheckCircle2, MessageCircle, X,
} from "lucide-react";
import { EditableText } from "../lib/content";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";

/* Convert a /api/media/... or /api/static/... path into a fully qualified URL. */
function absUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  if (url.startsWith("/")) return `${base}${url}`;
  return url;
}

const IDEAL_CUSTOMERS = [
  {
    icon: Briefcase,
    title: "You've built something worth protecting",
    body:
      "Your business name already has value. Your customers recognise it. You use it in your marketing. You've invested time, money, and effort into building it. The last thing you want is someone else trading under a confusingly similar name and pulling customers away.",
  },
  {
    icon: MapPin,
    title: "You rely on your name to win work",
    body:
      "Whether you operate locally, regionally, or online, your name is often the first thing customers see and remember. Trades, hair and beauty, hospitality, healthcare, fitness, professional services, retail. If your customers find you by name, this service is relevant to you.",
  },
  {
    icon: Heart,
    title: "You want protection that makes sense in the real world",
    body:
      "Trade marks can be powerful, but they are not always the right fit. They can be expensive, slow, and too narrow for how many small UK businesses actually trade. You want something practical. Straightforward. Affordable. And easy to understand.",
  },
  {
    icon: Sparkles,
    title: "You want to avoid a costly rebrand later",
    body:
      "If you are forced to change your business name, the cost is not just financial. It is disruption, lost visibility, and customer confusion. For many UK small businesses, a rebrand can cost £4,000 to £20,000+, plus months of lost momentum. Preventing that risk early is often the smarter decision.",
  },
];

const PRODUCT_INCLUDES = [
  "A dated, structured record of your business name use",
  "Monitoring support where similar names appear",
  "Up to £10,000 of passing-off legal assistance if your name is copied",
  "Access to a UK-based team who can support and guide you",
];

const WHY_BUILD_UP = [
  "Confused customers",
  "Lost enquiries",
  "Incorrect reviews",
  "Marketing spend benefiting the wrong business",
];

const TRADE_MARK_FIT = [
  "Practical, ongoing protection",
  "A lower-cost alternative or complement to trade marks",
  "A simple way to reduce risk around their trading name",
];

const FAQ = [
  {
    q: "Does Companies House protect my business name?",
    a: "No. Companies House only checks for identical names. It does not prevent similar or confusing names from being registered.",
  },
  {
    q: "Is this a trade mark?",
    a: "No. This is a separate service designed to provide practical protection and support around your business name.",
  },
  {
    q: "What does the £10,000 assistance include?",
    a: "It provides support towards legal action in passing-off situations where another business uses a confusingly similar name.",
  },
  {
    q: "Who is this for?",
    a: "UK small and medium-sized businesses that rely on their trading name to attract and retain customers.",
  },
  {
    q: "Can I still register a trade mark?",
    a: "Yes. Many businesses use both. We can also advise when a trade mark is the better option.",
  },
];

export default function About() {
  const [team, setTeam] = useState([]);
  const [openMemberId, setOpenMemberId] = useState(null);
  const loc = useLocation();

  useSeo({
    title: "About NBR Group - The UK's Business Name Protection Specialists",
    description: "NBR Group has been protecting UK business names for over a decade. 2000+ businesses trust us. Meet the team behind Business Name Protection and learn why we built it.",
  });

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loc.hash) return;
    const id = loc.hash.slice(1);
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(t);
  }, [loc.hash, team.length]);

  // Close the team-bio modal on Escape + lock body scroll while open.
  useEffect(() => {
    if (!openMemberId) return;
    const onKey = (e) => { if (e.key === "Escape") setOpenMemberId(null); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openMemberId]);

  const openMember = team.find((m) => m.id === openMemberId) || null;

  return (
    <main data-testid="about-page">
      {/* ───────────── HERO ───────────── */}
      <section className="relative overflow-hidden bnp-radial-glow pt-14 md:pt-20 pb-12">
        <div className="absolute inset-0 bnp-grid-bg opacity-30 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 relative text-center">
          <span className="bnp-chip bnp-chip-teal">NBR Business Name Protection</span>
          <h1 className="font-display text-[clamp(2rem,4vw,3.4rem)] text-[#04273d] mt-4 font-semibold tracking-tight leading-[1.08]">
            <EditableText keyName="about.hero.title" default="The UK's only dedicated business name protection service" />
          </h1>
          <p className="text-[#447694] text-base md:text-lg mt-5 leading-relaxed">
            <EditableText
              keyName="about.hero.p1"
              default="Business Name Protection is a service of NBR Group, a UK business services firm trusted by thousands of independent businesses, accountants and trade associations."
            />
          </p>
          <p className="text-[#447694] text-base md:text-lg mt-4 leading-relaxed">
            <EditableText
              keyName="about.hero.p2"
              default="Your business name is not just a label. It is your reputation, your marketing, and how customers find and recommend you."
            />
          </p>
          <p className="text-[#447694] text-base md:text-lg mt-4 leading-relaxed">
            <EditableText
              keyName="about.hero.p3"
              default="But in the UK, similar business names can legally exist side by side, and that creates risk most business owners only discover when it is too late."
            />
          </p>
          <p className="text-[#447694] text-base md:text-lg mt-4 leading-relaxed">
            <EditableText
              keyName="about.hero.p4"
              default="Business Name Protection gives you a simple, affordable way to help protect your business name before problems arise."
            />
          </p>
          <p className="font-display text-[#04273d] mt-6 font-bold text-lg md:text-xl">
            <EditableText keyName="about.hero.price" default="Start protecting your business name for £15 per month (including VAT)." />
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/pricing" className="btn-primary" data-testid="about-hero-protect-button">
              Protect your business name <ArrowRight size={14} />
            </Link>
            <Link to="/check" className="btn-secondary" data-testid="about-hero-check-link">
              Run a free check
            </Link>
          </div>
        </div>
      </section>

      {/* ───────────── ABOUT NBR GROUP (combined with team - first section after hero to establish trust immediately) ───────────── */}
      <section id="nbr" className="py-10 md:py-20 lg:py-24 xl:py-28 bg-gradient-to-br from-white via-white to-[#71edc9]/10 scroll-mt-24" data-testid="about-nbr-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 text-center mb-6 md:mb-10">
          <span className="bnp-chip bnp-chip-light">About NBR Group</span>
          <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
            Trusted by thousands of UK businesses
          </h2>
          <p className="text-[#447694] mt-4 text-base md:text-lg leading-relaxed">
            <EditableText
              keyName="about.nbr.body"
              default="Business Name Protection is provided by NBR Group, a UK business services firm working with thousands of independent businesses, accountants, and trade associations."
            />
          </p>
        </div>

        {/* Trust badge row - sits directly above the team grid to signal experience + reach at a glance */}
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 mb-6 md:mb-8" data-testid="about-team-badge-row">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[#9ac7f7]/40 shadow-sm px-4 py-2">
              <Award size={16} className="text-[#0d9b80] shrink-0" />
              <span className="font-display text-sm md:text-base text-[#04273d] font-bold whitespace-nowrap">40+ years UK IP experience</span>
            </span>
            <span className="hidden sm:inline text-[#9ac7f7] font-display text-lg leading-none" aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[#9ac7f7]/40 shadow-sm px-4 py-2">
              <Users size={16} className="text-[#0d9b80] shrink-0" />
              <span className="font-display text-sm md:text-base text-[#04273d] font-bold whitespace-nowrap">2000+ businesses protected</span>
            </span>
          </div>
        </div>

        {/* Team grid - photos first, narrative below, to establish trust as soon as visitors land */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          {team.length > 0 ? (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="about-team-grid">
              {team.map((m) => {
                const hasBio = Boolean(m.bio);
                return (
                  <article
                    key={m.id}
                    className={`bnp-card overflow-hidden flex flex-col ${hasBio ? "cursor-pointer hover:-translate-y-0.5 transition-transform" : ""}`}
                    data-testid={`about-team-member-${m.id}`}
                    onClick={() => hasBio && setOpenMemberId(m.id)}
                    onKeyDown={(e) => {
                      if (!hasBio) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpenMemberId(m.id);
                      }
                    }}
                    role={hasBio ? "button" : undefined}
                    tabIndex={hasBio ? 0 : undefined}
                    aria-haspopup={hasBio ? "dialog" : undefined}
                  >
                    <div className="aspect-square bg-[#f7f7f7] flex items-center justify-center overflow-hidden">
                      {m.photo_url ? (
                        <img src={absUrl(m.photo_url)} alt={m.name} className="w-full h-full object-cover" />
                      ) : (
                        <Users size={36} className="text-[#9ac7f7]" />
                      )}
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <p className="font-display font-bold text-[#04273d] text-base leading-snug">{m.name}</p>
                      <p className="text-xs text-[#447694] mt-0.5 font-medium">{m.job_title}</p>
                      {hasBio && (
                        <span
                          className="inline-flex items-center gap-1 mt-3 text-xs font-display font-semibold text-[#0d9b80] group-hover:gap-1.5"
                          data-testid={`about-team-member-${m.id}-readbio`}
                        >
                          Read bio <ArrowRight size={13} />
                        </span>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="bnp-card p-8 md:p-10 text-center" data-testid="about-team-placeholder">
              <Users size={28} className="text-[#9ac7f7] mx-auto" />
              <p className="font-display text-[#04273d] mt-3 font-semibold">Team profiles coming soon.</p>
              <p className="text-sm text-[#447694] mt-1">Sophie can add team photos and roles from the admin area.</p>
            </div>
          )}
        </div>

        {/* NBR + team narrative */}
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 mt-12 space-y-4 text-[#447694] text-base md:text-lg leading-relaxed">
          <p>
            NBR Group is a long-established UK business services firm specialising in intellectual property, trade marks, and business name protection. We specialise in helping businesses protect and manage their intellectual property in a way that is practical, affordable, and relevant to how they actually trade.
          </p>
          <p>
            For over 10 years, we have supported UK businesses in protecting the names and brands they trade under every day, from sole traders and start-ups through to established SMEs and professional service firms.
          </p>
          <p>
            Business Name Protection brings that experience together into one simple, affordable product designed specifically for everyday UK businesses.
          </p>
          <p className="pt-2">
            <a
              href="https://nationalbusinessregister.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[#0d9b80] hover:text-[#0a7e68] font-display font-semibold underline underline-offset-[3px] decoration-[1.5px]"
              data-testid="about-nbr-external-link"
            >
              Visit nationalbusinessregister.co.uk <ExternalLink size={14} />
            </a>
          </p>
        </div>

        {/* Sub-section: A team you can speak to */}
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 mt-10">
          <div className="bnp-card p-6 md:p-8">
            <h3 className="font-display text-xl md:text-2xl text-[#04273d] font-bold">A team you can speak to, not just a platform you use</h3>
            <p className="text-[#447694] mt-3 leading-relaxed">
              Behind Business Name Protection is a real UK-based team who understand how small businesses actually operate.
            </p>
            <p className="text-[#447694] mt-3 leading-relaxed">
              When you contact us, you are speaking to people who deal with business names, brand protection, and IP risk every day. Not automated systems or offshore call centres.
            </p>
            <div className="mt-4 rounded-2xl bg-[#f7f7f7] border border-[#9ac7f7]/25 px-4 py-3">
              <p className="text-[10px] font-mono-brand uppercase tracking-widest text-[#447694] font-semibold">Our role is simple</p>
              <p className="text-[#04273d] font-display font-semibold mt-1 leading-relaxed">
                Help you understand your risk, protect your business name, and support you if issues arise.
              </p>
            </div>
          </div>
        </div>

        {/* Trust stat cards */}
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 mt-10">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bnp-card p-5 text-center">
              <Users size={18} className="text-[#71edc9] mx-auto" />
              <p className="font-display text-2xl text-[#04273d] mt-2 font-bold">2000+</p>
              <p className="text-xs text-[#447694] mt-1">UK businesses protected</p>
            </div>
            <div className="bnp-card p-5 text-center">
              <Award size={18} className="text-[#71edc9] mx-auto" />
              <p className="font-display text-2xl text-[#04273d] mt-2 font-bold">40+ years</p>
              <p className="text-xs text-[#447694] mt-1">UK IP experience</p>
            </div>
            <div className="bnp-card p-5 text-center">
              <Building2 size={18} className="text-[#71edc9] mx-auto" />
              <p className="font-display text-2xl text-[#04273d] mt-2 font-bold">UK only</p>
              <p className="text-xs text-[#447694] mt-1">Jurisdictional focus</p>
            </div>
            <div className="bnp-card p-5 text-center">
              <PiggyBank size={18} className="text-[#71edc9] mx-auto" />
              <p className="font-display text-2xl text-[#04273d] mt-2 font-bold">£15/mo</p>
              <p className="text-xs text-[#447694] mt-1">Starting from</p>
            </div>
          </div>
        </div>

        {/* Why this matters banner */}
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 mt-12">
          <div className="rounded-3xl bg-gradient-to-br from-[#04273d] via-[#0d4d6e] to-[#0d9b80] text-white p-8 md:p-10" data-testid="about-why-matters-banner">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0 border border-white/20">
                <Shield size={22} className="text-[#71edc9]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-mono-brand text-[10.5px] uppercase tracking-widest text-[#71edc9]">Why this matters</p>
                <h3 className="font-display text-xl md:text-2xl font-bold mt-1">
                  Business Name Protection is more than a product. It is backed by a team that understands what happens when business names are challenged, copied, or confused.
                </h3>
                <p className="text-sm md:text-base text-white/80 mt-3 leading-relaxed">
                  We help businesses avoid problems where possible, and support them if issues arise.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── ABOUT YOU ───────────── */}
      <section id="you" className="py-16 md:py-20 lg:py-24 xl:py-28 scroll-mt-24" data-testid="about-you-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="bnp-chip bnp-chip-light">About you</span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
              <EditableText keyName="about.you.title" default="This service is built for UK businesses like yours" />
            </h2>
            <p className="text-[#447694] mt-4 text-base md:text-lg leading-relaxed">
              <EditableText
                keyName="about.you.body"
                default="Business Name Protection is designed for everyday UK business owners whose name is their brand. Not global corporations with legal teams and five-figure trade mark portfolios. Real businesses building something valuable and local. If this sounds like you, you are in the right place."
              />
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {IDEAL_CUSTOMERS.map((c, i) => (
              <div key={i} className="bnp-card p-6 hover:-translate-y-1 transition-transform" data-testid={`about-you-${i}`}>
                <div className="h-11 w-11 rounded-xl bg-[#71edc9]/30 flex items-center justify-center">
                  <c.icon size={20} className="text-[#04273d]" />
                </div>
                <h3 className="font-display text-base text-[#04273d] mt-4 font-semibold leading-snug">{c.title}</h3>
                <p className="text-sm text-[#447694] mt-2 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── ABOUT THE PRODUCT ───────────── */}
      <section id="product" className="py-16 md:py-20 lg:py-24 xl:py-28 bg-white border-y border-[#9ac7f7]/25 scroll-mt-24" data-testid="about-product-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center mb-10">
            <span className="bnp-chip bnp-chip-light">About the product</span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
              <EditableText
                keyName="about.product.title"
                default="A practical, affordable layer of protection for your business name"
              />
            </h2>
          </div>
          <div className="space-y-5 text-[#447694] text-base md:text-lg leading-relaxed">
            <p><EditableText keyName="about.product.p1" default="Business Name Protection is not a trade mark." /></p>
            <p><EditableText keyName="about.product.p2" default="It is a structured, preventative service designed to support UK businesses that want sensible protection around their trading name without unnecessary legal complexity." /></p>
            <p><EditableText keyName="about.product.p3" default="Most business owners assume Companies House protects their name. It does not." /></p>
            <p><EditableText keyName="about.product.p4" default="Companies House only checks for identical names. It does not assess similar or confusingly close names, which means near-identical businesses can still be registered and operate." /></p>
            <p className="text-[#04273d] font-semibold"><EditableText keyName="about.product.p5" default="That is where Business Name Protection comes in." /></p>
          </div>

          <div className="bnp-card mt-8 p-6 md:p-8">
            <p className="font-display font-bold text-[#04273d]">For £15 per month (including VAT), you get:</p>
            <ul className="mt-4 space-y-3">
              {PRODUCT_INCLUDES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm md:text-base text-[#447694]" data-testid="about-product-includes-item">
                  <CheckCircle2 size={18} className="text-[#0d9b80] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <p className="text-[#447694] mt-5 leading-relaxed">
              This is designed to give you a practical layer of cover around the name you trade under every day.
            </p>
          </div>

          {/* Honest advice */}
          <div className="mt-10 rounded-3xl border-2 border-[#71edc9]/40 bg-[#71edc9]/10 p-6 md:p-8" data-testid="about-honest-advice">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-2xl bg-[#04273d] flex items-center justify-center shrink-0">
                <MessageCircle size={20} className="text-[#71edc9]" />
              </div>
              <div>
                <h3 className="font-display text-xl md:text-2xl text-[#04273d] font-bold">Honest advice when you need it</h3>
                <p className="text-[#447694] mt-2 leading-relaxed">
                  We will always tell you if a trade mark is the better option for your situation.
                </p>
                <p className="text-[#447694] mt-2 leading-relaxed">
                  Some businesses do need trade mark protection. When that is the case, we will say so.
                </p>
                <p className="text-[#447694] mt-2 leading-relaxed">
                  But for many small UK businesses, this service provides a more proportionate, affordable, and realistic first layer of protection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── WHY THIS MATTERS ───────────── */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28" data-testid="about-why-matters-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center mb-10">
            <span className="bnp-chip bnp-chip-light">Why this matters</span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
              Most businesses act after the problem starts
            </h2>
            <p className="text-[#447694] mt-4 text-base md:text-lg leading-relaxed">
              When two businesses use similar names, the impact is rarely immediate. It builds quietly.
            </p>
          </div>
          <div className="bnp-card p-6 md:p-8">
            <ul className="grid sm:grid-cols-2 gap-3">
              {WHY_BUILD_UP.map((w) => (
                <li key={w} className="flex items-start gap-2.5 text-sm md:text-base text-[#04273d]">
                  <AlertTriangle size={18} className="text-amber-500 mt-0.5 shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
            <p className="text-[#447694] mt-5 leading-relaxed">
              By the time action is taken, the cost is often already higher than prevention.
            </p>
            <p className="text-[#04273d] font-semibold mt-3 leading-relaxed">
              Business Name Protection is designed to reduce that risk before it becomes a problem.
            </p>
          </div>
        </div>
      </section>

      {/* ───────────── SIMPLE PRICING ───────────── */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28 bg-gradient-to-br from-[#f7f7f7] via-white to-[#71edc9]/10" data-testid="about-pricing-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 text-center">
          <span className="bnp-chip bnp-chip-light">Simple pricing</span>
          <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
            One clear monthly cost
          </h2>
          <p className="font-display text-4xl md:text-5xl text-[#04273d] mt-5 font-bold">
            £15 per month <span className="text-base md:text-lg text-[#447694] font-semibold">(including VAT)</span>
          </p>
          <p className="text-[#447694] mt-4 text-base md:text-lg leading-relaxed">
            No complicated tiers. No hidden extras. No unnecessary add-ons.
          </p>
          <p className="text-[#04273d] font-semibold mt-2">
            Just straightforward protection for your business name.
          </p>
          <div className="mt-7">
            <Link to="/pricing" className="btn-primary" data-testid="about-pricing-cta">
              Start protection for £15/month <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ───────────── IS THIS A TRADE MARK ───────────── */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28" data-testid="about-trademark-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center mb-10">
            <span className="bnp-chip bnp-chip-light">Is this a trade mark?</span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
              No, and that is intentional.
            </h2>
          </div>
          <div className="space-y-5 text-[#447694] text-base md:text-lg leading-relaxed">
            <p>A trade mark is a formal legal right that protects your brand in specific classes and categories. It can be extremely powerful, but it is not always necessary or suitable for every small business.</p>
            <p className="text-[#04273d] font-semibold">Business Name Protection is designed for businesses that want:</p>
          </div>
          <ul className="bnp-card p-6 md:p-7 mt-5 space-y-3">
            {TRADE_MARK_FIT.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm md:text-base text-[#447694]">
                <CheckCircle2 size={18} className="text-[#0d9b80] mt-0.5 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <p className="text-[#447694] mt-6 leading-relaxed">
            We are always transparent about the difference. We will always recommend a trade mark when it is the right step.
          </p>
        </div>
      </section>

      {/* ───────────── FAQs ───────────── */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28 bg-white border-y border-[#9ac7f7]/25" data-testid="about-faq-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center mb-8">
            <span className="bnp-chip bnp-chip-light">FAQs</span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
              Common questions
            </h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <div key={i} className="bnp-card p-5 md:p-6" data-testid={`about-faq-${i}`}>
                <p className="font-display font-bold text-[#04273d]">{f.q}</p>
                <p className="text-sm md:text-base text-[#447694] mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── FINAL CTA ───────────── */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="bnp-cta-band rounded-3xl p-10 md:p-12 text-center relative">
            <Shield size={36} className="text-[#04273d] mx-auto relative" />
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-4 font-bold tracking-tight relative">
              <EditableText keyName="about.cta.title" default="Ready to protect your business name?" />
            </h2>
            <p className="text-[#04273d]/80 mt-3 max-w-xl mx-auto font-medium relative">
              <EditableText keyName="about.cta.body" default="Your business name is one of your most valuable assets. Protecting it early is simple. Waiting until there is a problem is expensive." />
            </p>
            <p className="font-display text-[#04273d] mt-4 font-bold text-base md:text-lg">
              Start today for £15 per month (including VAT).
            </p>
            <div className="mt-7 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 relative">
              <Link
                to="/pricing"
                className="bg-[#04273d] text-white hover:bg-[#0a3b59] transition-colors px-6 py-3.5 rounded-full font-display font-bold text-sm inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                data-testid="about-cta-protect-now"
              >
                Protect your business name now <ArrowRight size={14} />
              </Link>
              <Link
                to="/check"
                className="bg-white text-[#04273d] hover:bg-[#f7f7f7] transition-colors px-6 py-3.5 rounded-full font-display font-bold text-sm inline-flex items-center justify-center gap-2 border-2 border-[#04273d] w-full sm:w-auto"
                data-testid="about-cta-check"
              >
                Check your business name
              </Link>
              <Link
                to="/pricing"
                className="bg-white text-[#04273d] hover:bg-[#f7f7f7] transition-colors px-6 py-3.5 rounded-full font-display font-bold text-sm inline-flex items-center justify-center gap-2 border-2 border-[#04273d] w-full sm:w-auto"
                data-testid="about-cta-pricing"
              >
                View pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── TEAM BIO MODAL ───────────── */}
      {openMember && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="team-bio-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`About ${openMember.name}`}
          onClick={() => setOpenMemberId(null)}
        >
          <div className="absolute inset-0 bg-[#04273d]/70 backdrop-blur-sm" />
          <div
            className="relative bnp-card w-full max-w-lg overflow-hidden bnp-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpenMemberId(null)}
              className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-[#04273d] shadow-md transition-colors"
              data-testid="team-bio-modal-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="sm:flex">
              <div className="h-56 sm:h-auto sm:w-2/5 sm:min-h-[300px] bg-[#f7f7f7] flex items-center justify-center overflow-hidden shrink-0">
                {openMember.photo_url ? (
                  <img src={absUrl(openMember.photo_url)} alt={openMember.name} className="w-full h-full object-cover" data-testid="team-bio-modal-photo" />
                ) : (
                  <Users size={48} className="text-[#9ac7f7]" />
                )}
              </div>
              <div className="p-6 sm:w-3/5">
                <p className="font-display font-bold text-[#04273d] text-xl leading-snug" data-testid="team-bio-modal-name">{openMember.name}</p>
                <p className="text-[11px] text-[#447694] mt-1 font-mono-brand uppercase tracking-widest">{openMember.job_title}</p>
                <div className="mt-4 max-h-[45vh] overflow-y-auto pr-1">
                  <p className="text-sm text-[#447694] leading-relaxed whitespace-pre-line" data-testid="team-bio-modal-bio">{openMember.bio}</p>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </main>
  );
}
